// Admin overlay — full-screen modal with tabbed sections.
//
// Mounted from App.tsx when the user clicks "Admin settings" in the
// user menu. Server-side every admin.* RPC re-checks the role, so
// even if a non-admin somehow opens this, they'll see "admin only"
// errors instead of acting on the system.
//
// Tabs mirror the design prototype: Users, Audit, Sessions, Stats,
// LLM, Backups. Each tab is a small self-contained component that
// owns its own data fetch + local state.

import { useEffect, useState } from "preact/hooks";
import "../../styles/admin.css";

import { Icon, type IconName } from "../../icons";
import type { Me } from "../../lib/use-me";
import { AdminUsersTab } from "./users";
import { AdminHouseholdsTab } from "./households";
import { AdminAuditTab } from "./audit";
import { AdminSessionsTab } from "./sessions";
import { AdminStatsTab } from "./stats";
import { AdminLlmTab } from "./llm";
import { AdminBackupsTab } from "./backups";
import { AdminDeviceLogsTab } from "./device-logs";

interface AdminViewProps {
  open: boolean;
  onClose: () => void;
  me: Me | null;
  notify: (msg: string) => void;
}

type Tab = "users" | "households" | "audit" | "sessions" | "stats" | "llm" | "backups" | "device-logs";

const TABS: { id: Tab; label: string; icon: IconName }[] = [
  { id: "users",       label: "Users",      icon: "people" },
  { id: "households",  label: "Households", icon: "home" },
  { id: "audit",       label: "Audit",      icon: "activity" },
  { id: "sessions",    label: "Sessions",   icon: "device" },
  { id: "stats",       label: "Stats",      icon: "chart" },
  { id: "llm",         label: "LLM",        icon: "sparkle" },
  { id: "backups",     label: "Backups",    icon: "database" },
  { id: "device-logs", label: "App logs",   icon: "settings" },
];

export function AdminView({ open, onClose, me, notify }: AdminViewProps) {
  const [tab, setTab] = useState<Tab>("users");
  // Mobile drill-in: tab LIST first, tap to reveal a tab's body + back.
  // Desktop shows tabs + body together (CSS ignores this off-mobile).
  const [entered, setEntered] = useState(false);
  const activeTab = TABS.find((t) => t.id === tab);

  useEffect(() => {
    if (open) { setTab("users"); setEntered(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="admin-bg" onClick={onClose}>
      <div className="admin-shell" data-entered={entered} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Admin">
        <header className="admin-head">
          <div className="admin-head-left">
            <span className="admin-head-badge"><Icon name="shield" size={17} /></span>
            <div>
              <h2 className="admin-head-title">Admin</h2>
              <div className="admin-head-meta">
                signed in as {me?.username || "?"} · {me?.role || "?"}
              </div>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close (Esc)" type="button">
            <Icon name="x" />
          </button>
        </header>

        <nav className="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-active={tab === t.id}
              onClick={() => { setTab(t.id); setEntered(true); }}
              type="button"
            >
              <Icon name={t.icon} size={14} />
              <span>{t.label}</span>
              <span className="admin-tab-chev" aria-hidden><Icon name="arrow-right" size={14} /></span>
            </button>
          ))}
        </nav>

        <div className="admin-body">
          {/* Mobile-only back row — returns to the tab list. Hidden on
              desktop (tabs + body are always visible there). */}
          <button className="admin-back" type="button" onClick={() => setEntered(false)}>
            <Icon name="arrow-left" size={16} />
            <span>{activeTab?.label ?? "Admin"}</span>
          </button>
          {tab === "users"      && <AdminUsersTab      notify={notify} meId={me?.username || null} />}
          {tab === "households" && <AdminHouseholdsTab notify={notify} />}
          {tab === "audit"      && <AdminAuditTab      notify={notify} />}
          {tab === "sessions" && <AdminSessionsTab notify={notify} />}
          {tab === "stats"    && <AdminStatsTab    notify={notify} />}
          {tab === "llm"      && <AdminLlmTab      notify={notify} />}
          {tab === "backups"  && <AdminBackupsTab  notify={notify} />}
          {tab === "device-logs" && <AdminDeviceLogsTab notify={notify} />}
        </div>
      </div>
    </div>
  );
}
